import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Icon } from '@cbrebuild/blocks';
import FileUploadAccordian from '../../file-upload-accordian';
import countries from '../../../../data/countries.json';
import states from '../../../../data/states.json';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';

import '../../lease-abstraction.scss';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import contactsService from '../../../../services/contacts-service';
import ContactAutocomplete from '../../../autocomplete/contact-autocomplete';
import { isPartyClient, isPartyPocRequired } from '../../../../utils/transaction-utils';

const getFilteredStates = (countryName = 'United States') => {
  const found = countries.find(c => c.display_name === countryName);
  return states.filter(s => s.country_id === found?.country_id);
};

const LandlordInformation = ({ forceExpanded, isLoaded, onExpand, storageKey }) => {
  const [dealContacts, setDealContacts] = useState([]);
  const suppressLandlordFirstNameBlurPatchRef = useRef(false);
  const suppressLandlordLastNameBlurPatchRef = useRef(false);

  const {
    isEnhancedFlow,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    findFieldRef,
    deal,
    landlordCompany,
    onLandlordCompanyChange,
    onLandlordCompanyBlur,
    landlordSearchState,
    onLandlordSuggestionSelect,
    closeLandlordSuggestions,
    triggerLandlordSearch,
    patchLandlordCompany,
    patchLandlordContact,
  } = useContext(LeaseAbstractionContext);

  useEffect(() => {
    if (!isLoaded || !isEnhancedFlow || !deal?.id) return;
    contactsService.getContacts({ deal: deal.id, ordering: '-is_primary', page_size: 25 })
      .then(res => setDealContacts((res && res.results) || []))
      .catch(() => setDealContacts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, isLoaded]);

  const handleLandlordContactSelect = (c) => {
    const phone = c.phone_numbers && c.phone_numbers[0] ? c.phone_numbers[0].number : '';
    const email = c.email_addresses && c.email_addresses[0] ? c.email_addresses[0].address : '';
    modalFieldChangeHandler('multipleFieldNames', {
      landlordPOCFirstName: { value: c.given_name || '' },
      landlordPOCLastName: { value: c.surname || '' },
      landlordPOCPhone: { value: phone },
      landlordPOCEmail: { value: email },
      landlordPOCJobTitle: { value: c.job_title || '' },
    }, 'multiple');
    patchLandlordContact({
      given_name: c.given_name || '',
      surname: c.surname || '',
      job_title: c.job_title || '',
      phone_numbers: c.phone_numbers && c.phone_numbers.length ? c.phone_numbers : [],
      email_addresses: c.email_addresses && c.email_addresses.length ? c.email_addresses : [],
    });
  };

  const partyName = deal && deal.deal_type === 'lease' ? 'Landlord' : 'Lessor';
  const isLandlordPocRequired = isPartyPocRequired(deal, 'landlord');
  const isLandlordOurClient = isPartyClient(deal, 'landlord');
  const hasPocFieldValue = fieldName => String(modalFieldState[fieldName]?.value || '').trim().length > 0;
  const getPocFieldError = (fieldName, requiredMessage) => {
    if (!modalFieldState[fieldName]?.value && showPageValidation && isLandlordPocRequired) return requiredMessage;
    if (!isLandlordPocRequired && !hasPocFieldValue(fieldName)) return '';
    return fieldErrorMap[fieldName] || '';
  };
  const landlordPOCFirstNameError = getPocFieldError('landlordPOCFirstName', 'First Name is required');
  const landlordPOCLastNameError = getPocFieldError('landlordPOCLastName', 'Last Name is required');
  const landlordPOCPhoneError = getPocFieldError('landlordPOCPhone', 'Phone is required');
  const landlordPOCEmailError = getPocFieldError('landlordPOCEmail', 'E-Mail Address is required');
  return (
    <FileUploadAccordian accordianTitle="Landlord Information" forceExpanded={forceExpanded} onExpand={onExpand} storageKey={storageKey}>
      {/* Company Name — with search-before-create dropdown */}
      <div className="file-modal-group-container">
        <div className="file-modal-group">
          <label className="acc-field-label">{isEnhancedFlow && <span className="acc-required-asterisk">* </span>}Company Name</label>
          <div className="company-name-search-wrapper">
            <input
              type="text"
              className={`acc-input-text${(!modalFieldState.landlordCompanyName?.value && showPageValidation) || fieldErrorMap.landlordCompanyName?.length > 0 ? ' acc-input-error' : ''}`}
              value={modalFieldState.landlordCompanyName?.value || ''}
              placeholder="Company Name"
              onChange={(event) => {
                modalFieldChangeHandler('landlordCompanyName', { value: event.target.value });
                onLandlordCompanyChange({ target: { name: 'name', value: event.target.value, type: 'text' } });
              }}
              onFocus={() => { if (triggerLandlordSearch) triggerLandlordSearch(); }}
              onBlur={(event) => {
                individualModalFieldChangeHandler({
                  child_value: event.target.value,
                  parent_key: 'landlord_seller_info',
                  child_key: modalFieldState.landlordCompanyName?.trackingID,
                  stateVariable: 'landlordCompanyName',
                });
                setTimeout(() => { if (closeLandlordSuggestions) closeLandlordSuggestions(); }, 150);
                onLandlordCompanyBlur({ target: { name: 'name', value: event.target.value, type: 'text' } });
              }}
            />
            {landlordSearchState?.loading && <div className="company-name-search-loading-bar" />}
            {landlordSearchState?.showSuggestions && landlordSearchState.suggestions.length > 0 && (
              <div className="company-name-search-dropdown">
                {landlordSearchState.suggestions.map(s => (
                  <div
                    key={s.id}
                    className="company-name-search-item"
                    role="option"
                    aria-selected="false"
                    tabIndex={0}
                    onMouseDown={() => {
                      onLandlordSuggestionSelect(s);
                      modalFieldChangeHandler('landlordCompanyName', { value: s.name });
                    }}
                  >
                    <span className="company-name-search-item__name">{s.name}</span>
                    {s.address && (
                      <span className="company-name-search-item__address">
                        {[s.address.address, s.address.city, s.address.territory, s.address.postal, s.address.country]
                          .filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {((!modalFieldState.landlordCompanyName?.value && showPageValidation) || fieldErrorMap.landlordCompanyName?.length > 0) && (
            <p className="acc-error-message">{fieldErrorMap.landlordCompanyName?.length > 0 ? fieldErrorMap.landlordCompanyName : 'Company Name is required'}</p>
          )}
        </div>
        {/* Extraction chip — enhanced flow only; legacy modal flow shows no chip here */}
        {isEnhancedFlow && modalFieldState.landlordCompanyName?.toolTip?.pageIndex?.length > 0 && (
          <div className="acc-extraction-chip">
            <span className="acc-extraction-chip__icon" aria-hidden="true" />
            {String(modalFieldState.landlordCompanyName.extractedValue || '').trim().toLowerCase()
              !== String(modalFieldState.landlordCompanyName.value || '').trim().toLowerCase()
              ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onClick={() => {
                    const val = modalFieldState.landlordCompanyName.extractedValue;
                    modalFieldChangeHandler('landlordCompanyName', { value: val });
                    patchLandlordCompany({ name: val });
                  }}
                >
                  {modalFieldState.landlordCompanyName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.landlordCompanyName.extractedValue}</span>
              )}
            <button
              type="button"
              className="acc-extraction-chip__page"
              onClick={() => {
                const pageIndex = parseInt(modalFieldState.landlordCompanyName.toolTip.pageIndex, 10);
                if (!Number.isNaN(pageIndex)) findFieldRef('landlordCompanyName', pageIndex);
              }}
            >
              Page {modalFieldState.landlordCompanyName.toolTip.pageIndex}
            </button>
          </div>
        )}
      </div>
      <LeaseAbstractionField
        key="landlordCountryName"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.landlordCountryName,
          label: 'Country',
          errorMessage: (!modalFieldState.landlordCountryName.value && showPageValidation) ? 'Country is required' : fieldErrorMap.landlordCountryName,
          key: 'landlordCountryName',
          options: countries,
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler(
            'multipleFieldNames',
            {
              landlordCountryName: {
                ...modalFieldState.landlordCountryName,
                value: event.value,
              },
              landlordStateName: {
                ...modalFieldState.landlordStateName,
                value: '',
              },
              filteredStatesForLandlord: {
                value: getFilteredStates(event.value),
              },
            },
            'multiple',
          );
          onLandlordCompanyChange({ target: { name: 'country', value: event.value, type: 'select-one' } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordCountryName.trackingID,
            stateVariable: 'landlordCountryName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('multipleFieldNames', {
            landlordCountryName: { ...modalFieldState.landlordCountryName, value: extractedVal },
            landlordStateName: { ...modalFieldState.landlordStateName, value: '' },
            filteredStatesForLandlord: { value: getFilteredStates(extractedVal) },
          }, 'multiple');
          patchLandlordCompany({ country: extractedVal, territory: null });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordAddressLine1"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.landlordAddressLine1,
          label: 'Address Line 1',
          errorMessage: (!modalFieldState.landlordAddressLine1.value && showPageValidation) ? 'Address Line 1 is required' : fieldErrorMap.landlordAddressLine1,
          key: 'landlordAddressLine1',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordAddressLine1', {
            value: event.target.value,
          });
          onLandlordCompanyChange({ target: { name: 'address', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onLandlordCompanyBlur({ target: { name: 'address', value: modalFieldState.landlordAddressLine1.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordAddressLine1.trackingID,
            stateVariable: 'landlordAddressLine1',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordAddressLine1', { value: extractedVal });
          patchLandlordCompany({ address: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordAddressLine2"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: modalFieldState.landlordAddressLine2,
          label: 'Address Line 2',
          errorMessage: fieldErrorMap.landlordAddressLine2,
          key: 'landlordAddressLine2',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordAddressLine2', {
            value: event.target.value,
          });
          onLandlordCompanyChange({ target: { name: 'address2', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onLandlordCompanyBlur({ target: { name: 'address2', value: modalFieldState.landlordAddressLine2.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordAddressLine2.trackingID,
            stateVariable: 'landlordAddressLine2',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordAddressLine2', { value: extractedVal });
          patchLandlordCompany({ address2: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordCity"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.landlordCity,
          label: 'City',
          errorMessage: (!modalFieldState.landlordCity.value && showPageValidation) ? 'City is required' : fieldErrorMap.landlordCity,
          key: 'landlordCity',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordCity', {
            value: event.target.value,
          });
          onLandlordCompanyChange({ target: { name: 'city', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onLandlordCompanyBlur({ target: { name: 'city', value: modalFieldState.landlordCity.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordCity.trackingID,
            stateVariable: 'landlordCity',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordCity', { value: extractedVal });
          patchLandlordCompany({ city: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordStateName"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.landlordStateName,
          label: 'State',
          errorMessage: (!modalFieldState.landlordStateName.value && showPageValidation) ? 'State is required' : fieldErrorMap.landlordStateName,
          key: 'landlordStateName',
          options: modalFieldState.filteredStatesForLandlord.value,
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordStateName', { value: event.value });
          onLandlordCompanyChange({ target: { name: 'territory', value: event.value, type: 'select-one' } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordStateName.trackingID,
            stateVariable: 'landlordStateName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          const stateEntry = states.find(s => s.display_name === extractedVal || s.value === extractedVal);
          const abbrev = stateEntry ? stateEntry.value : extractedVal;
          modalFieldChangeHandler('landlordStateName', { value: abbrev, chipDisplayValue: extractedVal });
          patchLandlordCompany({ territory: abbrev });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordZip"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.landlordZip,
          label: 'Zip',
          errorMessage: (!modalFieldState.landlordZip.value && showPageValidation) ? 'Zip is required' : fieldErrorMap.landlordZip,
          key: 'landlordZip',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordZip', {
            value: event.target.value,
          });
          onLandlordCompanyChange({ target: { name: 'postal', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onLandlordCompanyBlur({ target: { name: 'postal', value: modalFieldState.landlordZip.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordZip.trackingID,
            stateVariable: 'landlordZip',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordZip', { value: extractedVal });
          patchLandlordCompany({ postal: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      {/* GWS Client */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">GWS Client</label>
          <input
            type="text"
            className="acc-input-text"
            name="gws_client"
            value={landlordCompany?.gws_client || ''}
            onChange={onLandlordCompanyChange}
            onBlur={onLandlordCompanyBlur}
          />
        </div>
      )}
      {/* Parent Company */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Parent Company</label>
          <input
            type="text"
            className="acc-input-text"
            name="parent_company"
            value={landlordCompany?.parent_company || ''}
            onChange={onLandlordCompanyChange}
            onBlur={onLandlordCompanyBlur}
          />
        </div>
      )}
      {/* Is Lessor/Landlord our client? */}
      {deal && (deal.deal_type === 'lease' || deal.deal_type === 'sale') && (
        <div className="file-modal-group-full">
          <Checkbox checked={!!isLandlordOurClient} disabled>
            Is {partyName} our client?
          </Checkbox>
        </div>
      )}
      <p className="file-modal-sub-heading">Point of Contact</p>
      {isEnhancedFlow && (() => {
        const cards = dealContacts.filter(c =>
          c.contact_type !== 'tenant' &&
          (c.given_name || c.surname) &&
          `${c.given_name || ''} ${c.surname || ''}`.trim() !== `${modalFieldState.landlordPOCFirstName?.value || ''} ${modalFieldState.landlordPOCLastName?.value || ''}`.trim()
        );
        return cards.length > 0 ? (
          <ul className="acc-deal-property-list">
            {cards.map(c => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={c.id}
                className="acc-deal-property-card"
                onClick={() => handleLandlordContactSelect(c)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLandlordContactSelect(c); }}
              >
                <div className="acc-deal-property-card__text">
                  <h4>{[c.given_name, c.surname].filter(Boolean).join(' ')}</h4>
                  <p>{c.job_title || (c.email_addresses && c.email_addresses[0]?.address) || ''}</p>
                </div>
                <span className="acc-add-icon">
                  <Icon iconName="plus-circle-outline" size="large" />
                </span>
              </li>
            ))}
          </ul>
        ) : null;
      })()}
      <p className="file-modal-note">Any changes made will be reflected on the Deal Contacts page.</p>
      {/* First Name */}
      {isEnhancedFlow ? (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${landlordPOCFirstNameError ? ' acc-field-has-error' : ''}`} style={{ minWidth: 0 }}>
            <label className="acc-field-label">
              {isLandlordPocRequired && <span className="acc-required-asterisk">* </span>}First Name
            </label>
            <ContactAutocomplete
              initSearchTerm={modalFieldState.landlordPOCFirstName.value || ''}
              onChange={(input) => {
                if (typeof input === 'string') {
                  modalFieldChangeHandler('landlordPOCFirstName', { value: input });
                } else {
                  suppressLandlordFirstNameBlurPatchRef.current = true;
                  handleLandlordContactSelect(input);
                }
              }}
              onBlur={() => {
                if (suppressLandlordFirstNameBlurPatchRef.current) {
                  suppressLandlordFirstNameBlurPatchRef.current = false;
                  return;
                }
                patchLandlordContact({ given_name: modalFieldState.landlordPOCFirstName.value });
              }}
              errorMessage={landlordPOCFirstNameError}
              placeholder="Search Contacts"
            />
          </div>
          {modalFieldState.landlordPOCFirstName.toolTip?.pageIndex?.length > 0 && (
            <div className="acc-extraction-chip">
              <span className="acc-extraction-chip__icon" aria-hidden="true" />
              {String(modalFieldState.landlordPOCFirstName.extractedValue || '').trim().toLowerCase() !== String(modalFieldState.landlordPOCFirstName.value || '').trim().toLowerCase() ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    modalFieldChangeHandler('landlordPOCFirstName', { value: modalFieldState.landlordPOCFirstName.extractedValue });
                    patchLandlordContact({ given_name: modalFieldState.landlordPOCFirstName.extractedValue });
                  }}
                >
                  {modalFieldState.landlordPOCFirstName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.landlordPOCFirstName.extractedValue}</span>
              )}
              <button
                type="button"
                className="acc-extraction-chip__page"
                onClick={() => findFieldRef('landlordPOCFirstName', parseInt(modalFieldState.landlordPOCFirstName.toolTip.pageIndex, 10))}
              >
                Page {modalFieldState.landlordPOCFirstName.toolTip.pageIndex}
              </button>
            </div>
          )}
        </div>
      ) : (
        <LeaseAbstractionField
          key="landlordPOCFirstName"
          fieldType={LeaseAbstractionFieldType.TEXT}
          fieldData={{
            data: modalFieldState.landlordPOCFirstName,
            label: 'First Name',
            errorMessage: fieldErrorMap.landlordPOCFirstName,
            key: 'landlordPOCFirstName',
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('landlordPOCFirstName', { value: event.target.value });
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'landlord_seller_info',
              child_key: modalFieldState.landlordPOCFirstName.trackingID,
              stateVariable: 'landlordPOCFirstName',
            });
          }}
        />
      )}
      {/* Last Name */}
      {isEnhancedFlow ? (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${landlordPOCLastNameError ? ' acc-field-has-error' : ''}`} style={{ minWidth: 0 }}>
            <label className="acc-field-label">
              {isLandlordPocRequired && <span className="acc-required-asterisk">* </span>}Last Name
            </label>
            <ContactAutocomplete
              initSearchTerm={modalFieldState.landlordPOCLastName.value || ''}
              onChange={(input) => {
                if (typeof input === 'string') {
                  modalFieldChangeHandler('landlordPOCLastName', { value: input });
                } else {
                  suppressLandlordLastNameBlurPatchRef.current = true;
                  handleLandlordContactSelect(input);
                }
              }}
              onBlur={() => {
                if (suppressLandlordLastNameBlurPatchRef.current) {
                  suppressLandlordLastNameBlurPatchRef.current = false;
                  return;
                }
                patchLandlordContact({ surname: modalFieldState.landlordPOCLastName.value });
              }}
              errorMessage={landlordPOCLastNameError}
              placeholder="Search Contacts"
            />
          </div>
          {modalFieldState.landlordPOCLastName.toolTip?.pageIndex?.length > 0 && (
            <div className="acc-extraction-chip">
              <span className="acc-extraction-chip__icon" aria-hidden="true" />
              {String(modalFieldState.landlordPOCLastName.extractedValue || '').trim().toLowerCase() !== String(modalFieldState.landlordPOCLastName.value || '').trim().toLowerCase() ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    modalFieldChangeHandler('landlordPOCLastName', { value: modalFieldState.landlordPOCLastName.extractedValue });
                    patchLandlordContact({ surname: modalFieldState.landlordPOCLastName.extractedValue });
                  }}
                >
                  {modalFieldState.landlordPOCLastName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.landlordPOCLastName.extractedValue}</span>
              )}
              <button
                type="button"
                className="acc-extraction-chip__page"
                onClick={() => findFieldRef('landlordPOCLastName', parseInt(modalFieldState.landlordPOCLastName.toolTip.pageIndex, 10))}
              >
                Page {modalFieldState.landlordPOCLastName.toolTip.pageIndex}
              </button>
            </div>
          )}
        </div>
      ) : (
        <LeaseAbstractionField
          key="landlordPOCLastName"
          fieldType={LeaseAbstractionFieldType.TEXT}
          fieldData={{
            data: modalFieldState.landlordPOCLastName,
            label: 'Last Name',
            errorMessage: fieldErrorMap.landlordPOCLastName,
            key: 'landlordPOCLastName',
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('landlordPOCLastName', { value: event.target.value });
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'landlord_seller_info',
              child_key: modalFieldState.landlordPOCLastName.trackingID,
              stateVariable: 'landlordPOCLastName',
            });
          }}
        />
      )}
      <LeaseAbstractionField
        key="landlordPOCPhone"
        fieldType={LeaseAbstractionFieldType.PHONE}
        isRequired={isLandlordPocRequired}
        fieldData={{
          data: modalFieldState.landlordPOCPhone,
          label: 'Phone',
          errorMessage: landlordPOCPhoneError,
          key: 'landlordPOCPhone',
        }}
        fieldChangeHandler={(value, isValid) => {
          modalFieldChangeHandler(
            'multipleFieldNames',
            {
              landlordPOCPhone: {
                ...modalFieldState.landlordPOCPhone,
                value,
              },
              landlordPOCPhoneValid: isValid,
            },
            'multiple',
          );
          if (isValid && value) {
            patchLandlordContact({
              phone_numbers: [{ type: 'work', number: value, name: '', default: true, isValid: true }],
            });
          } else if (!value) {
            patchLandlordContact({ phone_numbers: [] });
          }
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordPOCPhone.trackingID,
            stateVariable: 'landlordPOCPhone',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('multipleFieldNames', {
            landlordPOCPhone: { ...modalFieldState.landlordPOCPhone, value: extractedVal },
            landlordPOCPhoneValid: true,
          }, 'multiple');
          patchLandlordContact({
            phone_numbers: [{
              type: 'work', number: extractedVal, name: '', default: true, isValid: true,
            }],
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordPOCEmail"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={isLandlordPocRequired}
        fieldData={{
          data: modalFieldState.landlordPOCEmail,
          label: 'E-Mail Address',
          errorMessage: landlordPOCEmailError,
          key: 'landlordPOCEmail',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordPOCEmail', {
            value: event.target.value,
          });
        }}
        onBlur={() => patchLandlordContact({
          email_addresses: [{ type: 'work', address: modalFieldState.landlordPOCEmail.value, name: '', default: true, isValid: true }],
        })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordPOCEmail.trackingID,
            stateVariable: 'landlordPOCEmail',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordPOCEmail', { value: extractedVal });
          patchLandlordContact({
            email_addresses: [{
              type: 'work', address: extractedVal, name: '', default: true, isValid: true,
            }],
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordPOCJobTitle"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: modalFieldState.landlordPOCJobTitle,
          label: 'Job Title',
          errorMessage: fieldErrorMap.landlordPOCJobTitle,
          key: 'landlordPOCJobTitle',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordPOCJobTitle', {
            value: event.target.value,
          });
        }}
        onBlur={() => patchLandlordContact({ job_title: modalFieldState.landlordPOCJobTitle.value })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'landlord_seller_info',
            child_key: modalFieldState.landlordPOCJobTitle.trackingID,
            stateVariable: 'landlordPOCJobTitle',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordPOCJobTitle', { value: extractedVal });
          patchLandlordContact({ job_title: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
    </FileUploadAccordian>
  );
};

LandlordInformation.propTypes = {
  forceExpanded: PropTypes.bool,
  isLoaded: PropTypes.bool,
  onExpand: PropTypes.func,
  storageKey: PropTypes.string,
};

LandlordInformation.defaultProps = {
  forceExpanded: false,
  isLoaded: false,
  onExpand: () => {},
  storageKey: '',
};

export default LandlordInformation;
